import React from "react";
import { Search as SearchIcon, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Search + brokerage + sort filter row for the Realtors page.
 *
 * All state is owned by the parent — this component only renders the
 * inputs and calls the setters. Extracted from Realtors.jsx purely for
 * file-size discipline; no logic change.
 */
export default function RealtorsFilters({
  search,
  onSearchChange,
  brokerageFilter,
  onBrokerageFilterChange,
  brokerages,
  sort,
  onSortChange,
}) {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-3 flex flex-col sm:flex-row gap-2"
      data-testid="realtors-filters"
    >
      <div className="relative flex-1">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search name, email, phone…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9 pr-9 min-h-[44px]"
          data-testid="realtors-search-input"
        />
        {search ? (
          <button
            type="button"
            onClick={() => onSearchChange("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-700"
            aria-label="Clear search"
            data-testid="realtors-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <Select value={brokerageFilter} onValueChange={onBrokerageFilterChange}>
        <SelectTrigger
          className="sm:w-56 min-h-[44px]"
          data-testid="brokerage-filter-select"
        >
          <SelectValue placeholder="All brokerages" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All brokerages</SelectItem>
          {brokerages
            .filter((b) => b.status !== "rejected")
            .map((b) => (
              <SelectItem key={b.id} value={b.id}>
                {b.name}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
      <Select value={sort} onValueChange={onSortChange}>
        <SelectTrigger
          className="sm:w-44 min-h-[44px]"
          data-testid="sort-select"
        >
          <SelectValue placeholder="Sort by…" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="name:asc">Name (A→Z)</SelectItem>
          <SelectItem value="name:desc">Name (Z→A)</SelectItem>
          <SelectItem value="brokerage:asc">Brokerage (A→Z)</SelectItem>
          <SelectItem value="enrichment:desc">Most enriched first</SelectItem>
          <SelectItem value="scan_count:desc">Most scans first</SelectItem>
          <SelectItem value="scan_count:asc">Never-scanned first</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
